/**
 * Small colour helpers used to derive a brand palette from one or two base
 * colours (see `createBrandKit.ts`). Hex in, hex out — no dependencies.
 */

const clamp = (n: number, min = 0, max = 255) => Math.min(max, Math.max(min, n))

const parseHex = (hex: string): [number, number, number] => {
  let h = hex.replace('#', '').trim()
  if (h.length === 3) h = h.replace(/(.)/g, '$1$1')
  const n = parseInt(h, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const toHex = (r: number, g: number, b: number) =>
  '#' +
  [r, g, b]
    .map((v) => clamp(Math.round(v)).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()

/** Mix `hex` towards `target` by `amount` (0 = hex, 1 = target). */
const mix = (hex: string, target: [number, number, number], amount: number) => {
  const [r, g, b] = parseHex(hex)
  return toHex(
    r + (target[0] - r) * amount,
    g + (target[1] - g) * amount,
    b + (target[2] - b) * amount,
  )
}

/** Move a colour towards white. */
export const lighten = (hex: string, amount: number) => mix(hex, [255, 255, 255], amount)

/** Move a colour towards black. */
export const darken = (hex: string, amount: number) => mix(hex, [0, 0, 0], amount)

/** WCAG relative luminance, 0 (black) – 1 (white). */
export const luminance = (hex: string) => {
  const [r, g, b] = parseHex(hex).map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Text colour that stays readable on `hex`. */
export const contrastText = (hex: string) => (luminance(hex) > 0.45 ? '#1A1A1A' : '#FFFFFF')
